import React, { useEffect, useState } from "react";
import { ClipLoader } from "react-spinners";
import CustomButton from "../../../components/Form/ArrowButton";
import PayPal from "../../../assets/Icons/Paypal.svg";
import ProgressBar from "../../../components/Form/ProgressBar";
import { refreshInProgressOrderCount } from "../../../components/Header/useInProgressOrderCount";
import Modal from "../../../components/Modal";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ChevronDown, ChevronUp, WalletCards } from "lucide-react";
import { useThunk } from "../../../hooks/useThunk";
import { clearCreateOrderDraft, createPaypalCheckout, createWalletPaypalCheckout, fetchOrderPaymentSummary, fetchUserOrderById, getUserWallet, payOrderWithWallet, setFormDataGlobState, setPendingPaymentOrderId } from "../../../store";//.
import showToast from "../../../utils/toastNotifications";
import { formatYYYY_MM_DD_HH_MM_A } from "../../../utils/dateFormating";
import { currencyFormatter, formatPagesSlidesRequirement } from "../../../utils/formatting";
import { getDiscountDisplayName, seasonalDiscount } from "../../../utils/seasonalDiscount";
import { ADITIONAL_SERVICE_LABELS, orderStatus, paymentMethods } from "../../../constants/commonConstants";
import { AditionalServiceCostCalculator } from "../../../utils/aditionalServiceCostCalculator";

const formatTopicSummary = (topic = "") => {
  const normalizedTopic = String(topic || "").trim();
  if (!normalizedTopic) return "-";

  const words = normalizedTopic.split(/\s+/);
  return words.length > 8 ? `${words.slice(0, 8).join(" ")}...` : normalizedTopic;
};

const formatAttachedFilesCount = (documents = []) => {
  const fileCount = Array.isArray(documents) ? documents.length : 0;
  if (fileCount === 1) return "1 file attached";
  if (fileCount > 1) return `${fileCount} files attached`;
  return "No files attached";
};

const SecurePayment = () => {

  const wallet = useSelector((state) => state.user.wallet)// wallet 
  const isWalletFetching = useSelector((state) => state.user.isWalletFetching) // wallet isFetching state
  const createOrder = useSelector((state) => state.createOrder);
  
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const orderIdParam = params['*'];
  const dispatch = useDispatch();
  
  // Progressbar functions
  const steps = [
    { fullLabel: "Calculation", shortLabel: "Calculation" },
    { fullLabel: "Paper Instructions", shortLabel: "Instructions" },
    { fullLabel: "Additional Services", shortLabel: "Services" },
    { fullLabel: "Secure Payment", shortLabel: "Payment" },
  ];

  // defining local states
  const [currentOrder, setCurrentOrder] = useState(null);
  const [doPayConfirmation, setDoPayConfirmation] = useState({bool: false, type: ''});

  const [message, setMessage] = useState(""); // Display a message to the user
  const [showServices, setShowServices] = useState(false);
  const [useWallet, setUseWallet] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState(null);
  
  // defining custom hooks
  const [doFetchUserOrderById, isFetchUserOrderById, fetchUserOrderByIdError] = useThunk(fetchUserOrderById);
  const [doCreatePaypalCheckout, isCreatePaypalCheckout, createPaypalCheckoutError] = useThunk(createPaypalCheckout);
  const [doPayOrderWithWallet, isPayOrderWithWallet, errorPayOrderWithWallet] = useThunk(payOrderWithWallet);
  const [doGetUserWallet, errorGetUserWallet] = useThunk(getUserWallet);
  const [doFetchPaymentSummary, isPaymentSummaryFetching] = useThunk(fetchOrderPaymentSummary);
  const [doCreateWalletPaypalCheckout, isCreateWalletPaypalCheckout] = useThunk(createWalletPaypalCheckout);

  // defining side efefcts
  // fetch the order details from the path variable or navigates to the calculation age if the order id not found
  useEffect(() => {
    if (!params['*'] || params['*']?.toString()?.length < 1) {
      navigate("/calculation");
    } else {
      fetchOrdersByIdMethod();
      if(!(isWalletFetching === null) && !(isWalletFetching === true)){ // avoid multiple fetching requests on page refresh
        fetchUserWalletMethod();
      }
    }
  }, []);

  useEffect(() => {
    const orderId = orderIdParam;
    const refresh = async () => {
      if (!orderId) return;
      const result = await doFetchPaymentSummary({ id: orderId });
      if (result?.success) setPaymentSummary(result?.response?.data);
    };
    window.addEventListener("focus", refresh);
    const onVisibilityChange = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [orderIdParam, doFetchPaymentSummary]);

  const handleBack = () => {
    const hasDraft = createOrder?.formData && Object.keys(createOrder.formData).length > 0;
    if (!hasDraft && currentOrder?.orderRequirements) {
      dispatch(setFormDataGlobState(currentOrder.orderRequirements));
      dispatch(setPendingPaymentOrderId(currentOrder?._id || params['*'] || ""));
    }
    navigate("/additional-services");
  };

  const handleProceed = () => {
    navigate("/success-payment");
  };

  // fetch order by it;s id
  const fetchOrdersByIdMethod = async (param) => {
    const result = await doFetchUserOrderById({ id: params['*'] });
    if (result?.success) {
      setCurrentOrder(result?.response?.data);
      fetchPaymentSummaryMethod(result?.response?.data?._id);
    } else {
      showToast('error', result?.error?.message)
    }
  };

  const fetchPaymentSummaryMethod = async (orderId = currentOrder?._id) => {
    if (!orderId) return;
    const result = await doFetchPaymentSummary({ id: orderId });
    if (result?.success) setPaymentSummary(result?.response?.data);
  };

  // fetching current user wallet
  const fetchUserWalletMethod = async() => {
    const result = await doGetUserWallet();
    if(!result?.success){      
      showToast("error", result?.error?.message || "Error Occurred")
    }
  };

  // do payment with the wallet
  const handleProceedWithWallet = async() => {
    setDoPayConfirmation({...doPayConfirmation, bool: false, type: ''})
    if(currentOrder){
      const result = await doPayOrderWithWallet({id: currentOrder?._id});
      if(result.success){
        refreshInProgressOrderCount();
        showToast("success", result?.response?.message || "Payment Done Successfully.");
        dispatch(clearCreateOrderDraft());
        fetchOrdersByIdMethod();
        fetchUserWalletMethod();
        navigate(
          `/thread?roomIdParam=${result?.response?.data?.id}&threadIdParam=${result?.response?.data?.threadId}`,
          {
            state: { backRedirect: "/dashboard" },
            replace: true 
          }
        );
      }else{
        showToast("error", result?.error?.message || "Error Occurred")
      }

    }else{
      showToast("error", "Order details not found!");
      return;
    }
  }

  // do payment with the paypal
  const handleProceedWithPayPal = async () => {
    setDoPayConfirmation({...doPayConfirmation, bool: false, type: ''})
    if (!currentOrder) {
      showToast("error", "Order details not found!");
      return;
    }

    // setLoading(true);

    // Prepare order data to send to the server
    const orderData = {
      items: [
        {
          id: currentOrder?._id,
          name: currentOrder?._id || "Unknown Item",
          description: currentOrder?.user?.typeOfPaper + ' ' +currentOrder?.orderRequirements?.academicLevel +' '+ currentOrder?.orderRequirements?.typeOfPaper || "Order Details",
          quantity: 1,
          unit_price: currentOrder?.price || "0.00",
        },
      ],
      totalAmount: currentOrder?.price || "0.00",
      currency: "USD",
    };

    try {
      const result = await doCreatePaypalCheckout(orderData);

      console.log("result from pp")
      console.log(result);
      
      if (result?.success) {
        const { approveUrl } = result?.response;
        window.location.href = approveUrl;
      } else {
        showToast("error", result?.error?.message || "Failed to create PayPal checkout session");
      }
    } catch (error) {
      showToast("error", error.message || "Error during payment initiation");
    }
  };

  const handleProceedWithWalletAndPayPal = async () => {
    setDoPayConfirmation({ bool: false, type: '' });
    const latest = await doFetchPaymentSummary({ id: currentOrder?._id });
    if (!latest?.success) return showToast("error", latest?.error?.message || "Unable to refresh payment amounts");
    const summary = latest.response?.data;
    setPaymentSummary(summary);
    if (summary?.walletContribution <= 0 || summary?.paypalRequired <= 0) {
      return showToast("error", "Your wallet balance changed. Please review the updated payment options.");
    }
    const result = await doCreateWalletPaypalCheckout({
      id: currentOrder?._id,
      expectedWalletAmount: summary.walletContribution,
      expectedPaypalAmount: summary.paypalRequired,
    });
    if (result?.success) window.location.href = result.response?.approveUrl;
    else showToast("error", result?.error?.message || "Failed to create PayPal checkout session");
  };
  const pagesSlidesRequirement = formatPagesSlidesRequirement(
    currentOrder?.orderRequirements?.pages,
    currentOrder?.orderRequirements?.slides
  );
  const orderRequirements = currentOrder?.orderRequirements || {};

 const getVisibleAdditionalServices = (services = []) =>
    services.filter((service) => !["Grammarly Report", "Writing Process Report"].includes(service?.label));

  const getAdditionalServiceDetail = (service) => {
    if (service?.label === ADITIONAL_SERVICE_LABELS.FIGURES) {
      const figures = currentOrder?.orderRequirements?.numberOfFigures || service?.perPageMultiplier;
      return figures ? `${figures} figure${Number(figures) === 1 ? "" : "s"} requested` : "";
    }

    if (service?.label === ADITIONAL_SERVICE_LABELS.SPECIFIC_WRITER) {
      const writerId =
        currentOrder?.orderRequirements?.requestedWriterId ||
        service?.requestedWriterId ||
        service?.writerId;
      return writerId ? `Writer ID requested: ${writerId}` : "";
    }

    return "";
  };

  const summaryItems = [
    {
      label: "Writing Type",
      value: `${orderRequirements?.writingType || "-"}`,
    },
    {
      label: "Type of Paper",
      value: `${orderRequirements?.typeOfPaper || "-"}`,
    },
    {
      label: "Academic Level",
      value: orderRequirements?.academicLevel || "-",
    },
    {
      label: "English Type",
      value: orderRequirements?.typeOfEnglish || "-",
    },
    {
      label: "Subject",
      value: orderRequirements?.subject || "-",
    },
    {
      label: "Topic",
      value: formatTopicSummary(orderRequirements?.topic),
    },
    {
      label: pagesSlidesRequirement.label,
      value: pagesSlidesRequirement.value,
    },
    {
      label: "Spacing",
      value: orderRequirements?.spacing || "-",
    },
    {
      label: "Reference Style",
      value: orderRequirements?.referenceStyle || "-",
    },
    {
      label: "Deadline",
      value: formatYYYY_MM_DD_HH_MM_A(
        orderRequirements?.deadline || ""
      ),
    },
    {
      label: "Files Attached",
      value: formatAttachedFilesCount(orderRequirements?.referenceDocuments),
    },
    orderRequirements?.isContinuationOrder && orderRequirements?.previousOrderId
      ? {
          label: "Previous Order ID",
          value: orderRequirements.previousOrderId,
        }
      : null,
  ].filter(Boolean);

  const discount = currentOrder?.discount;
  const showDiscount = seasonalDiscount.enabled && discount?.amount > 0;
  const priceBeforeDiscount = discount?.amount > 0
    ? Number((Number(currentOrder?.price || 0) + Number(discount.amount || 0)).toFixed(2))
    : currentOrder?.price;
  const walletAvailable = Number(paymentSummary?.walletAvailable ?? wallet?.amount ?? 0);
  const walletContribution = Math.min(walletAvailable, Number(currentOrder?.price || 0));
  const paypalRequired = Math.max(0, Number(currentOrder?.price || 0) - walletContribution);
  const isPaymentProcessing = isCreatePaypalCheckout || isPayOrderWithWallet || isCreateWalletPaypalCheckout;
  const paymentWasCancelled = ["cancelled", "cancel"].includes(searchParams.get("paymentStatus") || searchParams.get("paymentSession"));

  return (
    <div className="flex min-w-[340px] flex-col flex-grow h-screen overflow-x-hidden">
      {isPaymentProcessing && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/35 px-4 backdrop-blur-[2px]">
          <div className="flex w-full max-w-[340px] flex-col items-center rounded-[20px] bg-white px-6 py-8 text-center shadow-[0_24px_70px_rgba(15,23,42,0.22)]">
            <ClipLoader
              loading={isPaymentProcessing}
              speedMultiplier={0.7}
              size={78}
              color="#60a5fa"
            />
            <h3 className="mt-5 text-[20px] font-semibold leading-7 text-[#0020BD]">
              Processing payment
            </h3>
            <p className="mt-2 text-sm leading-6 text-gray">
              Please wait while we securely complete your payment.
            </p>
          </div>
        </div>
      )}
      <div className="text-primary font-roboto font-semibold text-[24px] pb-4 ml-[24px] xl:mt-[24px] mt-[104px]">
        <h2 className="xl:hidden">Dashboard</h2>
      </div>
      <div className="overflow-y-auto xl:mt-10 mb-[48px]">
        <div className=" bg-white mt-[32px] font-inter flex flex-col justify-center items-center   lg:mx-[48px] mx-[24px]  border-[1px] border-medium-gray  rounded-lg py-6  md:py-7  max-w-full  md:max-w-[2200px] px-[16px] md:px-[24px] lg:px-[48px] xl:px-0 w-auto   ">
          <div className="w-full flex items-center flex-col ">
            <div className="grid grid-cols-1 gap-x-6 w-full justify-center items-center ">
              <div className="mb-6 ">
                <ProgressBar steps={steps} activeStep={4} completedStep={3} />{" "}
              </div>

              {paymentWasCancelled && (
                <div className="mx-auto mb-4 w-full max-w-[664px] rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium leading-5 text-amber-800" role="status">
                  Payment was cancelled. No PayPal payment was completed. You can safely choose a payment option below.
                </div>
              )}

              <div className="flex flex-col w-full   items-center">
                <div className="w-full  flex-col items-center sm:w-[500px] md:w-[620px] lg:w-[580px] xl:w-[664px] flex px-0 xs:px-4 sm:px-0 ">
                  <div className="w-full rounded-[20px] border border-medium-blue bg-gradient-to-br from-[#fdfefe] via-[#f8fbff] to-[#eef4ff] px-3.5 py-4 shadow-[0_18px_48px_rgba(0,32,189,0.08)] sm:px-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <span className="inline-flex rounded-full bg-blue-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0020BD]">
                          Final Step
                        </span>
                        <h3 className="mt-2 text-[18px] font-semibold leading-6 text-primary">
                          Order Summary
                        </h3>
                        <p className="mt-0.5 text-xs text-gray">
                          Review your order details before completing payment.
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 gap-px overflow-hidden rounded-[14px] border border-[#dfe7ff] bg-[#dfe7ff] sm:grid-cols-2">
                      {!isFetchUserOrderById && summaryItems.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex min-w-0 items-center justify-between gap-3 bg-white/95 px-3 py-2"
                        >
                          <div className="shrink-0 text-[9px] font-semibold uppercase tracking-[0.08em] text-[#6e7ba8]">
                            {item?.label}
                          </div>
                          <div className="min-w-0 break-words text-right text-[12px] font-medium leading-4 text-secondary" title={item?.value}>
                            {item?.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {
                     !isFetchUserOrderById && 
                          getVisibleAdditionalServices(currentOrder?.orderRequirements?.aditionalServices).length > 0 &&
                  <>
                    <div
                      className="mt-3 flex w-full cursor-pointer select-none items-center justify-between rounded-[14px] border border-[#dfe7ff] bg-white px-3.5 py-2 text-primary shadow-[0_10px_28px_rgba(15,23,42,0.05)]"
                      onClick={() => setShowServices(!showServices)}
                    >
                      <div>
                        <span className="block text-[14px] font-semibold leading-5">
                          Additional Services
                        </span>
                        <span className="block text-xs font-normal text-gray">
                          {getVisibleAdditionalServices(currentOrder?.orderRequirements?.aditionalServices).length || 0} selected
                        </span>
                      </div>
                      {showServices ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </div>
                    { showServices &&
                      <div className="mt-2 w-full overflow-auto rounded-[16px] border border-[#dfe7ff] bg-white p-2 font-inter shadow-[0_14px_36px_rgba(15,23,42,0.05)] scrollbar-thin">
                      {
                        (
                            getVisibleAdditionalServices(currentOrder.orderRequirements.aditionalServices).map((service, idx) => (
                                <div
                                  key={idx}
                                  className="mb-2 flex items-center justify-between gap-3 rounded-lg border border-[#edf2ff] bg-[#f9fbff] px-3 py-2 last:mb-0"
                                >
                                  <div className="min-w-0 pr-2">
                                    <span className="block truncate text-[13px] font-semibold leading-5 text-[#2F3464]">
                                      {service?.label}
                                    </span>
                                    {getAdditionalServiceDetail(service) && (
                                      <span className="mt-0.5 block text-[11px] font-medium leading-4 text-gray">
                                        {getAdditionalServiceDetail(service)}
                                      </span>
                                    )}
                                  </div>
                                <span className="shrink-0 whitespace-nowrap rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-[#0020BD]">
                                  {currencyFormatter.format(Number(
                                      AditionalServiceCostCalculator(
                                        service, 
                                        currentOrder?.basePrice, 
                                        currentOrder?.costPerPage, 
                                        currentOrder?.orderRequirements?.pages
                                      )
                                    ).toFixed(2) || 0)}
                                </span>
                            </div>
                            )
                          ))
                      }
                    </div>
                    }
                  </>
                  }
                    <div
                        className="mt-4 grid w-full grid-cols-2 items-center gap-3 rounded-[16px] border border-[#cdd8ff] bg-gradient-to-r from-[#eef3ff] to-[#f8fbff] px-4 py-3 shadow-[0_16px_36px_rgba(0,32,189,0.10)] sm:flex sm:justify-between"
                      >
                      <div className="min-w-0">
                        <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-[#4c62c7]">
                          Total Due
                        </span>
                        <span className="block mt-0.5 text-[16px] font-semibold leading-6 text-[#0020BD]">
                          Order Total
                        </span>
                      </div>
                      <div className="min-w-0 text-right">
                        {showDiscount && (
                          <span className="block text-[14px] font-medium leading-5 text-blue-shades/70 line-through">
                            {currencyFormatter.format(priceBeforeDiscount || 0)}
                          </span>
                        )}
                        <span className="block text-[20px] font-semibold leading-7 text-[#0020BD]">
                          {currencyFormatter.format(currentOrder?.price || 0) || "$ 00.00"}
                        </span>
                        {showDiscount && (
                          <span className="block break-words text-[13px] font-semibold leading-5 text-blue-shades">
                            You saved {currencyFormatter.format(discount.amount)} with {getDiscountDisplayName(discount.name)}
                          </span>
                        )}
                      </div>
                    </div>
                    {isFetchUserOrderById && (
                        <>
                          <div className="flex justify-center items-center w-full h-[100%]">
                            <ClipLoader
                              loading={isFetchUserOrderById}
                              speedMultiplier={0.5}
                              size={82}
                              color="#60a5fa"
                            /> 
                          </div>
                        </>
                      )
                    }

                  <div className="mt-4 w-full rounded-[18px] border border-[#dfe7ff] bg-white p-4 shadow-sm">
                    <label className={`flex items-start gap-3 ${walletAvailable > 0 ? "cursor-pointer" : "cursor-not-allowed opacity-60"}`}>
                      <input type="checkbox" className="mt-1 h-4 w-4 accent-[#0020BD]" checked={useWallet} disabled={walletAvailable <= 0 || isPaymentSummaryFetching} onChange={(event) => setUseWallet(event.target.checked)} />
                      <span className="flex-1">
                        <span className="flex items-center gap-2 font-semibold text-secondary"><WalletCards size={19} /> Use my wallet balance</span>
                        <span className="mt-1 block text-sm text-gray">Available: {currencyFormatter.format(walletAvailable)}</span>
                      </span>
                    </label>
                    {paymentSummary?.activeReservation && !paymentSummary.activeReservation.isCurrentOrder && useWallet && (
                      <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">Continuing will move your wallet reservation from the earlier unpaid checkout to this order. The earlier draft will remain unpaid and recalculate when reopened.</p>
                    )}
                    {useWallet && walletAvailable > 0 && (
                      <div className="mt-4 space-y-2 border-t border-[#edf2ff] pt-3 text-sm">
                        <div className="flex justify-between"><span>Wallet contribution</span><strong>-{currencyFormatter.format(walletContribution)}</strong></div>
                        <div className="flex justify-between"><span>PayPal or Card</span><strong>{currencyFormatter.format(paypalRequired)}</strong></div>
                      </div>
                    )}
                  </div>

                  {useWallet && walletContribution > 0 && (
                    <button type="button" disabled={currentOrder?.status !== orderStatus.PENDING_PAYMENT || isPaymentProcessing} onClick={() => setDoPayConfirmation({ bool: true, type: paypalRequired > 0 ? paymentMethods.WALLET_PAYPAL : paymentMethods.WALLET })} className="mt-4 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-[10px] bg-[#0020BD] px-4 text-sm font-semibold text-white shadow-md transition hover:bg-[#00178a] disabled:opacity-60">
                      <WalletCards size={20} />
                      {paypalRequired > 0 ? <><span>Use {currencyFormatter.format(walletContribution)} + Pay {currencyFormatter.format(paypalRequired)} with</span><img src={PayPal} alt="PayPal" className="h-[16px] w-[64px] brightness-0 invert" /><span>or Card</span></> : <span>Pay {currencyFormatter.format(walletContribution)} from Wallet</span>}
                    </button>
                  )}

                  {useWallet && walletContribution > 0 && (
                    <div className="mt-3 flex w-full items-center gap-3" aria-hidden="true">
                      <span className="h-px flex-1 bg-[#d7deef]" />
                      <span className="text-[11px] font-semibold tracking-[0.16em] text-[#667085]">OR</span>
                      <span className="h-px flex-1 bg-[#d7deef]" />
                    </div>
                  )}

                  <button type="button" disabled={currentOrder?.status !== orderStatus.PENDING_PAYMENT || isPaymentProcessing} onClick={() => setDoPayConfirmation({ bool: true, type: paymentMethods.PAYPAL })} className={`${useWallet && walletContribution > 0 ? "mt-3" : "mt-4"} flex min-h-[48px] w-full items-center justify-center gap-2 rounded-[10px] px-4 text-sm font-semibold shadow-sm transition-all duration-200 disabled:opacity-60 ${!useWallet ? "bg-[#FFC43A] text-[#253B80] hover:bg-[#f2b91e]" : "border border-[#b9c8ee] bg-white text-[#253B80] hover:border-[#0020BD] hover:bg-[#eaf0ff] hover:text-[#00178a] hover:shadow-md"}`}>
                    <span>Pay {currencyFormatter.format(currentOrder?.price || 0)} with</span><img src={PayPal} alt="PayPal" className="h-[16px] w-[64px]" /><span>or Card</span>
                  </button>

                  <span className="text-level sm:text-[15px] text-gray font-inter font-normal leading-[18.15px] mt-5">
                    {message}
                  </span>
                  
                  <div className="font-roboto flex justify-between flex-row gap-1.5 mt-10 sm:mt-12 w-full">
                    <CustomButton
                      text="Back"
                      iconDirection="left"
                      iconPosition="before"
                      disabled={isPaymentProcessing}
                      onClick={handleBack}
                      width="clamp(76px, 24vw, 104px)"
                      buttonClassName="gap-2 px-2 text-[12px] hover:bg-black disabled:hover:bg-slate-300 sm:px-3"
                      iconClassName="h-6 w-6 shrink-0"
                    />
                    {currentOrder?.status !== orderStatus.PENDING_PAYMENT && <CustomButton
                      text="Continue"
                      iconDirection="right"
                      iconPosition="after"
                      disabled={isPaymentProcessing}
                      onClick={() => navigate(`/thread?roomIdParam=${currentOrder?.id}&threadIdParam=${currentOrder?.threadId}`)}
                      width="clamp(112px, 34vw, 140px)"
                      buttonClassName="gap-1 px-1.5 text-[12px] sm:gap-[12px] sm:px-[30px]"
                      iconClassName="h-3 w-3 sm:h-4 sm:w-4"
                    />}
                    {/* <CustomButton
                      text={
                        <>
                          <span className="sm:hidden">Next</span>{" "}
                          <span className="hidden sm:inline">
                            Pay Now
                          </span>{" "}
                        </>
                      }
                      iconDirection="right"
                      iconPosition="after"
                      width="60px sm:260px"
                      onClick={handleProceed}
                      classname="transition-all duration-500 ease-in-out"
                      disabled={true}
                    /> */}
                  </div>
                 
                </div>
              </div>
            </div>
          </div>
        </div>{" "}
      </div>
      {/* confirmation modela */}
      <Modal
        isOpen={doPayConfirmation.bool}
        onClose={() => setDoPayConfirmation({...doPayConfirmation, bool: false})}
        onConfirm={doPayConfirmation.type === paymentMethods.PAYPAL ? handleProceedWithPayPal : doPayConfirmation.type === paymentMethods.WALLET ? handleProceedWithWallet : doPayConfirmation.type === paymentMethods.WALLET_PAYPAL ? handleProceedWithWalletAndPayPal : () => {}}
        title="Please Confirm the Payment"
        content="Are you sure you want to proceed the payment?"
        cancelText="Cancel"
        confirmText="Confirm"
        buttonType="success"
      />
    </div>
  );
};

export default SecurePayment;
